import * as React from "react"
import * as Primitives from "@radix-ui/react-radio-group"
import { clx } from "../utils/clx"
// import { Label } from "../label"

/**
 * This component is based on the [Radix UI Radio Group](https://www.radix-ui.com/primitives/docs/components/radio-group) primitives.
 */
const Root = React.forwardRef<
  React.ElementRef<typeof Primitives.Root>,
  React.ComponentPropsWithoutRef<typeof Primitives.Root>
>(({ className, ...props }, ref) => {
  return (
    <Primitives.Root
      className={clx("grid gap-2", className)}
      {...props}
      ref={ref}
    />
  )
})
Root.displayName = "RadioGroup"

const Indicator = React.forwardRef<
  React.ElementRef<typeof Primitives.Indicator>,
  React.ComponentPropsWithoutRef<typeof Primitives.Indicator>
>(({ className, ...props }, ref) => {
  return (
    <Primitives.Indicator
      ref={ref}
      className={clx("flex items-center justify-center", className)}
      {...props}
    >
      <div
        className={clx(
          "bg-ui-bg-base group-disabled:bg-ui-fg-disabled h-1.5 w-1.5 rounded-full group-disabled:shadow-none"
        )}
      />
    </Primitives.Indicator>
  )
})
Indicator.displayName = "RadioGroup.Indicator"

interface ItemProps
  extends React.ComponentPropsWithoutRef<typeof Primitives.Item> {
  label: string
}

const Item = React.forwardRef<
  React.ElementRef<typeof Primitives.Item>,
  ItemProps
>(({ className, id, label, ...props }, ref) => {
  if (!id) {
    id = React.useId()
  }

  return (
    <Primitives.Item
      ref={ref}
      className={clx(
        "transition-fg p-0 focus-visible:outline-none disabled:bg-ui-bg-disabled group flex items-start gap-x-2 p-3 disabled:cursor-not-allowed",
        className
      )}
      {...props}
      id={id}
    >
      <div className="flex h-5 w-5 items-center justify-center">
        <div className="border border-grey-30 bg-ui-bg-base group-hover:bg-ui-bg-base-hover group-data-[state=checked]:bg-blue-base group-focus-visible:shadow-buttons-focus focus-visible:shadow-none group-data-[state=checked]:border-none group-disabled:!bg-ui-bg-disabled group-disabled:!border-grey-10 transition-fg flex h-4 w-4 items-center justify-center rounded-full">
          <Indicator />
        </div>
      </div>
      <label
        htmlFor={id}
        className="txt-base-regular-plus group-disabled:text-ui-fg-faint cursor-pointer group-disabled:cursor-not-allowed"
      >
        {label}
      </label>
    </Primitives.Item>
  )
})
Item.displayName = "RadioGroup.Item"

const RadioGroup = Object.assign(Root, { Item })

export { RadioGroup }
